import React from "react";
import Modal from "react-modal";
import DataTable, { type TableColumn } from "react-data-table-component";
import CustomButton from "../../buttons/CustomButton";
import infoSizeStyles from "../styles/infoSizeStyles";
import NoRecord from "@components/layouts/NoRecord";
import TableSkeleton from "@components/layouts/TableSkeleton";
import OutlinedStatus from "@components/status/OutlinedStatus";
import { thousandSeperator } from "@utils/general/digit-separators";
import { formatDate } from "@utils/general/format-date";

interface OverdueInvoicesModalProps {
    data: any;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isLoading?: boolean;
}

const getInvoiceNumber = (item: any) =>
    item?.secondary_sales_invoice_code ||
    item?.invoice_no ||
    item?.invoice_number ||
    item?.invoice_id ||
    item?.id ||
    "N/A";

const getStatus = (status: any) => {
    if (status === 1 || status === "1" || status === "Open") return "Open";
    if (status === 2 || status === "2" || status === "Partial Paid" || status === "Partial") {
        return "Partial Paid";
    }
    if (status === 3 || status === "3" || status === "Paid") return "Paid";
    return typeof status === "string" ? status : "Overdue";
};

const getStatusColor = (status: string) => {
    if (status === "Paid") return "green";
    if (status === "Partial Paid") return "purple";
    return "blue";
};

const overdueInvoiceColumns: TableColumn<any>[] = [
    {
        name: "Invoice ID",
        cell: (row: any) => (
            <div className="font-semibold text-primary-dark">
                {getInvoiceNumber(row)}
            </div>
        ),
        sortable: true,
        grow: 2.5,
    },
    {
        name: "Sales Order ID",
        cell: (row: any) => (
            <div className="font-semibold text-primary-dark">
                {row?.secondary_sales_order_code || row?.sales_order_code || "N/A"}
            </div>
        ),
        sortable: true,
        grow: 2.5,
    },
    {
        name: "Invoice Date",
        selector: (row: any) =>
            row?.invoice_date ? formatDate(new Date(row.invoice_date)) : "N/A",
        sortable: true,
        grow: 1.5,
    },
    {
        name: "Due Date",
        selector: (row: any) =>
            row?.due_date ? formatDate(new Date(row.due_date)) : "N/A",
        sortable: true,
        grow: 1.5,
    },
    {
        name: "Grand Total Amount",
        selector: (row: any) =>
            thousandSeperator(
                row?.grand_total_amount ||
                    row?.amount ||
                    row?.balance_amount ||
                    row?.outstanding_amount ||
                    row?.current_amount ||
                    0,
            ),
        sortable: true,
    },
    {
        name: "Paid Amount",
        selector: (row: any) =>
            thousandSeperator(row?.total_payment_receive_amount || 0),
        sortable: true,
    },
    {
        name: "Balance",
        selector: (row: any) => thousandSeperator(row?.balance || 0),
        sortable: true,
    },
    {
        name: "Status",
        cell: (row: any) => {
            const status = getStatus(row?.status || row?.payment_status || row?.invoice_status);

            return (
                <OutlinedStatus
                    width="w-fit"
                    title={status}
                    color={getStatusColor(status)}
                />
            );
        },
        sortable: true,
        grow: 1.4,
    },
];

const OverdueInvoicesModal: React.FC<OverdueInvoicesModalProps> = ({
    data,
    isOpen,
    setIsOpen,
    isLoading,
}) => {
    const invoices = Array.isArray(data) ? data : data?.data || [];

    return (
        <Modal
            isOpen={isOpen}
            style={{
                ...infoSizeStyles,
                content: {
                    ...infoSizeStyles.content,
                    maxWidth: "90%",
                    width: "1100px",
                },
            }}
            contentLabel="Overdue Invoices"
            onRequestClose={() => setIsOpen(false)}
        >
            <div className="flex h-full flex-col">
                <div className="flex items-start justify-between px-6 py-4 md:px-14">
                    <h3 className="text-xl font-[600] uppercase text-primary-dark">
                        Overdue Invoices
                    </h3>

                    <svg
                        onClick={() => setIsOpen(false)}
                        xmlns="http://www.w3.org/2000/svg"
                        height="20px"
                        viewBox="0 -960 960 960"
                        width="20px"
                        fill="#3e3e3e"
                        className="cursor-pointer"
                    >
                        <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                    </svg>
                </div>

                <div className="flex-1 overflow-y-auto px-6 pb-4 md:px-14">
                    {isLoading ? (
                        <TableSkeleton col={8} row={8} />
                    ) : (
                        <DataTable
                            keyField="secondary_sales_invoice_id"
                            responsive
                            striped
                            noDataComponent={<NoRecord />}
                            columns={overdueInvoiceColumns}
                            data={invoices}
                        />
                    )}
                </div>

                <div className="mr-4 flex justify-end pb-6">
                    <CustomButton
                        handleClick={() => setIsOpen(false)}
                        color="bg-primary-dark"
                        className="px-8 py-3"
                        title="Close"
                    />
                </div>
            </div>
        </Modal>
    );
};

export default OverdueInvoicesModal;
